/**
 * HelpGuide overlay
 *
 * Renders the in-app novice user guide. The guide content is a single
 * Markdown source (`@/content/userGuide.md`) bundled at build time via Vite's
 * `?raw` import — the same file is mirrored by `docs/guides/USER_GUIDE.md`.
 * Screenshots live in `frontend/public/userguide/` (served at `/userguide/*`);
 * the Markdown references them with a repo-relative path so `docs-check` and
 * GitHub resolve a real file, and `rewriteImageSrc` below maps that path to the
 * served URL for in-app rendering.
 *
 * Layout mirrors the Settings modal: a left table-of-contents sidebar (built
 * from the Markdown headings, with a filter box) and a scrollable rendered
 * content panel. Clicking a TOC entry — or an in-page anchor link — scrolls the
 * matching heading into view rather than mutating the page URL.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { X, BookOpen, Search } from 'lucide-react';
import { cn } from '@/utils/classNames';
import guideMarkdown from '@/content/userGuide.md?raw';

interface HelpGuideProps {
  isOpen: boolean;
  onClose: () => void;
}

export interface TocEntry {
  level: 2 | 3;
  text: string;
  slug: string;
}

/**
 * GitHub-style heading slug. Used for heading `id`s, TOC targets, and the
 * in-page `#anchor` links in the Markdown — all three must agree, so this is
 * the single definition.
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-');
}

/** Flatten a React children tree to its plain text (for heading ids). */
export function nodeText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (typeof node === 'object' && 'props' in node) {
    return nodeText((node as { props: { children?: ReactNode } }).props.children);
  }
  return '';
}

/**
 * Build the table of contents from the raw Markdown. Tracks fenced code blocks
 * so a `## ` inside a code sample is never mistaken for a heading.
 */
export function buildToc(markdown: string): TocEntry[] {
  const entries: TocEntry[] = [];
  let inFence = false;
  for (const line of markdown.split('\n')) {
    if (/^\s*```/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const match = /^(#{2,3})\s+(.*\S)\s*$/.exec(line);
    if (!match) continue;
    const level = match[1].length as 2 | 3;
    const text = match[2].trim();
    entries.push({ level, text, slug: slugify(text) });
  }
  return entries;
}

/** Map a Markdown image path to the app-served `/userguide/<file>` URL. */
export function rewriteImageSrc(src: string | undefined): string | undefined {
  if (!src) return src;
  const marker = '/userguide/';
  const idx = src.lastIndexOf(marker);
  if (idx !== -1) return src.slice(idx);
  return src;
}

const HEADING_SIZES: Record<'h1' | 'h2' | 'h3', string> = {
  h1: 'text-2xl font-bold mt-2 mb-4',
  h2: 'text-xl font-bold mt-8 mb-3 pt-2 border-t border-border-default first:border-t-0 first:mt-2',
  h3: 'text-base font-semibold mt-5 mb-2',
};

/**
 * The `react-markdown` component overrides, parameterized by the scroll
 * handler so in-page anchor links can scroll the guide. Exported so the
 * renderers can be unit-tested in isolation.
 */
export function createMarkdownComponents(
  scrollToSlug: (slug: string) => void
): Components {
  const makeHeading = (Tag: 'h1' | 'h2' | 'h3') => {
    const HeadingComponent = ({ children }: { children?: ReactNode }) => (
      <Tag
        id={slugify(nodeText(children))}
        className={cn('text-text-primary scroll-mt-4', HEADING_SIZES[Tag])}
      >
        {children}
      </Tag>
    );
    HeadingComponent.displayName = `GuideHeading(${Tag})`;
    return HeadingComponent;
  };

  return {
    h1: makeHeading('h1'),
    h2: makeHeading('h2'),
    h3: makeHeading('h3'),
    p: ({ children }) => (
      <p className="my-3 leading-relaxed text-text-secondary">{children}</p>
    ),
    a: ({ href, children }) => {
      if (href && href.startsWith('#')) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              scrollToSlug(href.slice(1));
            }}
            className="text-stellar-primary hover:underline"
          >
            {children}
          </a>
        );
      }
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-stellar-primary hover:underline"
        >
          {children}
        </a>
      );
    },
    ul: ({ children }) => (
      <ul className="my-3 list-disc space-y-1 pl-6 text-text-secondary">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="my-3 list-decimal space-y-1 pl-6 text-text-secondary">
        {children}
      </ol>
    ),
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    strong: ({ children }) => (
      <strong className="font-semibold text-text-primary">{children}</strong>
    ),
    blockquote: ({ children }) => (
      <blockquote className="my-4 rounded-r-lg border-l-4 border-stellar-primary bg-bg-secondary px-4 py-2 text-text-secondary">
        {children}
      </blockquote>
    ),
    hr: () => <hr className="my-6 border-border-default" />,
    img: ({ src, alt }) => (
      <img
        src={rewriteImageSrc(typeof src === 'string' ? src : undefined)}
        alt={alt ?? ''}
        loading="lazy"
        className="my-4 max-w-full rounded-lg border border-border-default shadow-sm"
      />
    ),
    video: ({ src, poster }) => (
      <video
        src={rewriteImageSrc(typeof src === 'string' ? src : undefined)}
        poster={rewriteImageSrc(typeof poster === 'string' ? poster : undefined)}
        controls
        preload="metadata"
        className="my-4 w-full rounded-lg border border-border-default shadow-sm"
      />
    ),
    code: ({ children }) => (
      <code className="rounded bg-bg-tertiary px-1.5 py-0.5 font-mono text-sm text-text-primary">
        {children}
      </code>
    ),
    table: ({ children }) => (
      <div className="my-4 overflow-x-auto">
        <table className="w-full border-collapse text-sm">{children}</table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-bg-secondary">{children}</thead>
    ),
    th: ({ children }) => (
      <th className="border border-border-default px-3 py-2 text-left font-semibold text-text-primary">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-border-default px-3 py-2 align-top text-text-secondary">
        {children}
      </td>
    ),
  };
}

/** Scroll the heading with the given slug into view, if it exists. */
function scrollToSlug(slug: string): void {
  const target = document.getElementById(slug);
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

export function HelpGuide({ isOpen, onClose }: HelpGuideProps) {
  const [filter, setFilter] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const toc = useMemo(() => buildToc(guideMarkdown), []);
  const filteredToc = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return toc;
    return toc.filter((e) => e.text.toLowerCase().includes(q));
  }, [toc, filter]);

  const components = useMemo(() => createMarkdownComponents(scrollToSlug), []);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Move focus into the dialog when it opens; reset the filter on close.
  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus();
    } else {
      setFilter('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="User guide"
        className="flex h-[640px] max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-bg-primary shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Table of contents */}
        <div className="hidden w-72 flex-col border-r border-border-default bg-bg-secondary p-4 sm:flex">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-text-primary">
            <BookOpen className="h-5 w-5 text-stellar-primary" />
            User Guide
          </h2>

          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
            <input
              ref={searchRef}
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Find a topic..."
              aria-label="Filter guide topics"
              className="w-full rounded-lg border border-border-default bg-bg-primary py-2 pl-9 pr-3 text-sm text-text-primary placeholder:text-text-tertiary focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary/30"
            />
          </div>

          <nav className="-mr-2 flex-1 space-y-0.5 overflow-y-auto pr-2">
            {filteredToc.length === 0 ? (
              <p className="px-3 py-2 text-sm text-text-tertiary">
                No topics match “{filter}”.
              </p>
            ) : (
              filteredToc.map((entry) => (
                <button
                  key={entry.slug}
                  onClick={() => scrollToSlug(entry.slug)}
                  className={cn(
                    'block w-full rounded-md px-3 py-1.5 text-left text-sm transition-colors',
                    'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary',
                    entry.level === 3 && 'pl-7 text-xs'
                  )}
                >
                  {entry.text}
                </button>
              ))
            )}
          </nav>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col">
          <div className="flex items-center justify-between border-b border-border-default px-6 py-3">
            <h2 className="flex items-center gap-2 text-base font-semibold text-text-primary sm:hidden">
              <BookOpen className="h-5 w-5 text-stellar-primary" />
              User Guide
            </h2>
            <span className="hidden text-sm text-text-tertiary sm:block">
              Press Esc to close
            </span>
            <button
              onClick={onClose}
              aria-label="Close user guide"
              className="rounded-lg p-1.5 text-text-tertiary hover:bg-bg-tertiary hover:text-text-secondary"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="mx-auto max-w-3xl">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={components}
              >
                {guideMarkdown}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
